import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import IgitaboLogo from "./IgitaboLogo";
import { useConfirmLogout } from "./ConfirmDialog";
import { findLeafPath } from "../navigation/navTree";

export default function AppLayout({
  title = "Umudugudu",
  groups,
  navItems = [],
  activeTab,
  onTabChange,
  onLogout,
  children,
  bottomNavItems,
  headerRight,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef  = useRef(null);
  const toggleRef   = useRef(null);
  const navigate    = useNavigate();
  const confirmLogout = useConfirmLogout();

  // ── Grouped navigation (used when `groups` is passed). Groups are always
  // fully expanded — every item stays directly visible/clickable, section
  // headings are static uppercase labels only, no collapsing or dropdowns.
  // `activePath` also powers the breadcrumb in the main content header. ──
  const activePath = useMemo(
    () => (groups ? findLeafPath(groups, activeTab) : null),
    [groups, activeTab]
  );

  useEffect(() => {
    if (!sidebarOpen) return;
    const handler = (e) => {
      if (sidebarRef.current?.contains(e.target) || toggleRef.current?.contains(e.target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const handler = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const handleTabChange = useCallback((key) => {
    onTabChange(key);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [onTabChange]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.clear();
    navigate("/", { replace: true });
    if (onLogout) onLogout();
  }, [navigate, onLogout]);

  const promptLogout = useCallback(async () => {
    const ok = await confirmLogout();
    if (ok) handleLogout();
  }, [confirmLogout, handleLogout]);

  const bnav = bottomNavItems || navItems.slice(0, 5);

  // Sidebar = one button per group (Level 1). No sub-items are shown here.
  // Clicking a group with a single child goes straight to that page;
  // clicking a group with several children opens that group's overview
  // page (main area). The button is "active" when the current tab is the
  // group itself or any of its children.
  const renderGroupButton = useCallback((group) => {
    const children = group.children || [];
    const destination = children.length === 1 ? children[0].key : group.key;
    const isActive = activeTab === group.key || children.some((c) => c.key === activeTab);
    return (
      <button
        key={group.key}
        onClick={() => handleTabChange(destination)}
        aria-current={isActive ? "page" : undefined}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer select-none text-left min-h-[44px]"
        style={isActive
          ? { background: "rgba(255,255,255,0.18)", color: "#ffffff", borderLeft: "3px solid #FFD700" }
          : { color: "rgba(255,255,255,0.7)", borderLeft: "3px solid transparent" }
        }
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
      >
        <i className={`bi ${group.icon} text-base flex-shrink-0 w-5 text-center`}
          style={{ color: isActive ? "#FFD700" : "rgba(255,255,255,0.6)" }} />
        <span className="flex-1 truncate">{group.label}</span>
        {children.length > 1 && (
          <i className="bi bi-chevron-right text-[0.7rem] flex-shrink-0"
            style={{ color: isActive ? "rgba(255,215,0,0.7)" : "rgba(255,255,255,0.3)" }} />
        )}
      </button>
    );
  }, [activeTab, handleTabChange]);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Brand */}
      <div className="px-5 py-5 flex-shrink-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}>
            <i className="bi bi-shield-fill text-white text-sm" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-white leading-tight truncate">{title}</p>

          </div>
        </div>
      </div>

      {/* Section label */}
      <div className="px-4 pt-4 pb-1 flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.45)" }}>Menu</span>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5 scrollbar-thin">
        {groups
          ? groups.map((group) => renderGroupButton(group))
          : navItems.map((item) => {
              const isActive = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => handleTabChange(item.key)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer select-none text-left min-h-[44px]"
                  style={isActive
                    ? { background: "rgba(255,255,255,0.18)", color: "#ffffff", borderLeft: "3px solid #FFD700" }
                    : { color: "rgba(255,255,255,0.7)", borderLeft: "3px solid transparent" }
                  }
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  <i className={`bi ${item.icon} text-base flex-shrink-0 w-5 text-center`}
                    style={{ color: isActive ? "#FFD700" : "rgba(255,255,255,0.6)" }} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="ml-auto text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none"
                      style={{ background: "#e53e3e" }}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
      </nav>

      {/* Konti / Logout */}
      <div className="flex-shrink-0 px-3 pb-3 pt-2" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
        <div
          className="flex items-center gap-2 select-none"
          style={{
            padding: "8px 0.75rem 5px 1rem",
            fontSize: "0.62rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.45)",
          }}
        >
          <i className="bi bi-person-circle flex-shrink-0" style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)" }} />
          <span className="flex-1 truncate">Konti</span>
        </div>
        <button
          onClick={promptLogout}
          className="w-full flex items-center gap-2.5 rounded-lg text-sm font-medium transition-all duration-200 min-h-[40px] text-left"
          style={{ padding: "0.5rem 0.75rem 0.5rem 1.25rem", color: "rgba(255,255,255,0.7)", borderLeft: "3px solid transparent" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.75)"; }}
        >
          <i className="bi bi-box-arrow-right text-sm flex-shrink-0 w-4 text-center" style={{ color: "rgba(255,255,255,0.55)" }} />
          <span className="flex-1 truncate">Sohoka</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#eef2f7" }}>

      {/* ── TOP HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-40 flex items-center px-4 gap-3"
        style={{
          height: "60px",
          background: "#003366",
          borderBottom: "3px solid #20603D",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}
      >
        {/* Hamburger */}
        <button
          ref={toggleRef}
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={sidebarOpen ? "Funga menu" : "Fungura menu"}
          aria-expanded={sidebarOpen}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-white hover:bg-white/10 transition-colors flex-shrink-0"
        >
          <i className={`bi bi-${sidebarOpen ? "x-lg" : "list"} text-lg`} />
        </button>

        {/* Brand — mobile */}
        <div className="flex items-center gap-2 flex-1 min-w-0 lg:hidden">
          <IgitaboLogo size={48} variant="full" />
        </div>

        {/* Brand — desktop */}
        <div className="hidden lg:flex flex-1 items-center">
          <IgitaboLogo size={54} variant="full" />
        </div>

        {headerRight && (
          <div className="flex items-center gap-2 flex-shrink-0">{headerRight}</div>
        )}

        {/* Logout — always visible */}
        <button
          onClick={promptLogout}
          className="flex items-center gap-1.5 rounded-lg font-semibold transition-colors flex-shrink-0"
          style={{
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.25)",
            fontSize: "0.78rem",
            padding: "0.45rem 0.75rem",
            minHeight: "36px",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
          aria-label="Sohoka"
        >
          <i className="bi bi-box-arrow-right" style={{ fontSize: "0.95rem" }} />
          <span>Sohoka</span>
        </button>
      </header>

      {/* ── SIDEBAR BACKDROP ── */}
      <div
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
        className={`
          fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden
          transition-opacity duration-300
          ${sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
      />

      {/* ── SIDEBAR ── */}
      <aside
        ref={sidebarRef}
        aria-label="Sidebar navigation"
        className={`
          fixed top-0 left-0 bottom-0 z-30 w-64 flex flex-col
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
        style={{
          paddingTop: "60px",
          background: "#003366",
          borderRight: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <SidebarContent />
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main
        className="flex-1 lg:ml-64 min-w-0"
        style={{
          paddingTop: "60px",
          paddingBottom: "calc(72px + env(safe-area-inset-bottom, 0px))",
          background: "#eef2f7",
        }}
      >
        <div className="p-4 md:p-5 lg:p-6 min-h-full">
          {groups && activePath && activePath.length > 1 && (
            <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1.5 mb-3 text-xs font-medium" style={{ color: "#5b6b82" }}>
              {activePath.map((node, i) => (
                <React.Fragment key={node.key}>
                  {i > 0 && <i className="bi bi-chevron-right" style={{ fontSize: "0.6rem", opacity: 0.6 }} />}
                  <span style={i === activePath.length - 1 ? { color: "#003366", fontWeight: 700 } : undefined}>
                    {node.label}
                  </span>
                </React.Fragment>
              ))}
            </nav>
          )}
          {children}
        </div>
      </main>

      {/* ── FLOATING BOTTOM NAV ── */}
      <nav
        aria-label="Mobile navigation"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Floating pill container */}
        <div
          className="mx-3 mb-3 rounded-2xl flex items-stretch overflow-hidden"
          style={{
            background: "#003366",
            boxShadow: "0 -2px 16px rgba(0,51,102,0.3), 0 8px 24px rgba(0,0,0,0.2)",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          {bnav.map((item) => {
            const isActive = activeTab === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleTabChange(item.key)}
                className={`
                  flex-1 flex flex-col items-center justify-center gap-1
                  py-2.5 px-1 relative transition-all duration-200
                  ${isActive ? "text-yellow-300" : "text-white/50 hover:text-white/80"}
                `}
              >
                {/* Active glow dot */}
                {isActive && (
                  <span
                    className="absolute top-1.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: "#FFD700", boxShadow: "0 0 6px #FFD700" }}
                  />
                )}
                <i
                  className={`bi ${item.icon} text-lg leading-none transition-transform duration-200
                    ${isActive ? "scale-110" : ""}`}
                />
                <span
                  className={`text-[9px] font-semibold uppercase tracking-wide leading-none truncate max-w-[52px]
                    ${isActive ? "text-yellow-300" : "text-white/50"}`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

    </div>
  );
}
